/**
 * Product Template Script
 * ------------------------------------------------------------------------------
 * A file that contains scripts highly couple code to the Product template.
 *
 * @namespace product
 */

import Variants from '@shopify/theme-variants';
import {imageSize, preload, getSizedImageUrl} from '@shopify/theme-images';
import {formatMoney} from '@shopify/theme-currency';
import sections from '@shopify/theme-sections';
import CartCount from '../modules/cart_count';

const selectors = {
  productForm: '[data-product-form]',
  addToCart: '[data-add-to-cart]',
  addToCartText: '[data-add-to-cart-text]',
  comparePrice: '[data-compare-price]',
  comparePriceText: '[data-compare-text]',
  originalSelectorId: '[data-product-select]',
  priceWrapper: '[data-price-wrapper]',
  productFeaturedImage: '[data-product-featured-image]',
  productJson: '[data-product-json]',
  productPrice: '[data-product-price]',
  productThumbs: '[data-product-single-thumbnail]',
  singleOptionSelector: '[data-single-option-selector]',
  singleOptionThumbnail: '[data-single-option-thumbnail]'
};

/**
 * Product section constructor. Runs on page load as well as Theme Editor
 * `section:load` events.
 * @param {string} container - selector for the section container DOM element
 */

sections.register('product', {
  onLoad() {
    // Stop parsing if we don't have the product json script tag when loading
    // section in the Theme Editor
    if (!$(selectors.productJson, this.$container).html()) {
      return;
    }

    this.cartCount = new CartCount();

    this.productSingleObject = JSON.parse(
      $(selectors.productJson, this.$container).html(),
    );

    const options = {
      $container: this.$container,
      enableHistoryState: this.$container.data('enable-history-state') || false,
      singleOptionSelector: selectors.singleOptionSelector,
      originalSelectorId: selectors.originalSelectorId,
      product: this.productSingleObject,
    };

    this.settings = {};
    this.variants = new Variants(options);
    this.$featuredImage = $(selectors.productFeaturedImage, this.$container);
    this.$featuredImageWidths = $(selectors.productFeaturedImage, this.$container).data('widths');
    this.$form = $(selectors.productForm, this.$container);
    this.$priceWrapper = $(selectors.priceWrapper, this.$container);
    this.$singleOptionThumbnail = $(selectors.singleOptionThumbnail, this.$container);
    this.$productThumbs = $(selectors.productThumbs, this.$container);

    this.$container.on(
      `variantChange${this.namespace}`,
      this.updateAddToCartState.bind(this),
      this.updateSingleOptionThumbnail.bind(this),
    );
    this.$container.on(
      `variantPriceChange${this.namespace}`,
      this.updateProductPrices.bind(this),
    );
    this.$singleOptionThumbnail.on(
      'click',
      this.updateSingleOptionSelect.bind(this),
    );
    this.$productThumbs.on(
      'click',
      this.updateProductImage.bind(this)
    );
    this.$form.on(
      'submit',
      this.updateCart.bind(this),
    );
    this.cartCount.update();

    if (this.$featuredImage.length > 0) {
      this.settings.imageSize = imageSize(this.$featuredImage[0].currentSrc);
      preload(this.productSingleObject.images, this.settings.imageSize);

      this.$container.on(
        `variantImageChange${this.namespace}`,
        this.updateProductImage.bind(this),
      );
    }
  },

  /**
   * Updates the DOM state of the add to cart button
   *
   * @param {boolean} enabled - Decides whether cart is enabled or disabled
   * @param {string} text - Updates the text notification content of the cart
   */
  updateAddToCartState(evt) {
    const variant = evt.variant;
    const $addToCart = $(selectors.addToCart, this.$container);
    const $addToCartText = $(selectors.addToCartText, this.$container);

    if (variant) {
      this.$priceWrapper.removeClass('hide');
    } else {
      $addToCart.prop('disabled', true);
      $addToCartText.html(
        theme.strings.unavailable,
      );
      this.$priceWrapper.addClass('hide');
      return;
    }

    if (variant.available) {
      $addToCart.prop('disabled', false);
      $addToCartText.html(theme.strings.addToCart);
    } else {
      $addToCart.prop('disabled', true);
      $addToCartText.html(theme.strings.soldOut);
    }
  },

  /**
   * Updates the DOM with specified prices
   *
   * @param {string} productPrice - The current price of the product
   * @param {string} comparePrice - The original price of the product
   */
  updateProductPrices(evt) {
    const variant = evt.variant;
    const $comparePrice = $(selectors.comparePrice, this.$container);
    const $compareEls = $comparePrice.add(
      selectors.comparePriceText,
      this.$container,
    );

    $(selectors.productPrice, this.$container).html(
      formatMoney(variant.price, theme.moneyFormat),
    );

    if (variant.compare_at_price > variant.price) {
      $comparePrice.html(
        formatMoney(variant.compare_at_price, theme.moneyFormat),
      );
      $compareEls.removeClass('hide');
    } else {
      $comparePrice.html('');
      $compareEls.addClass('hide');
    }
  },

  /**
   * Updates the DOM with the specified image URL
   *
   * @param {string} src - Image src URL
   */
  updateProductImage(evt) {
    let rawImgSrc = null;
    let sizedImgSrc = null;
    let varientSrcset = [];

    if (evt.variant) {
      rawImgSrc = evt.variant.featured_image.src;
    } else {
      // if there's no variant, grab the src and remove Shopify's default size parameter ('_small') added by the 'img_url' filter
      // https://help.shopify.com/en/themes/liquid/filters/url-filters
      rawImgSrc = evt.currentTarget.getAttribute('data-img-src').replace('_small', '');
    }

    sizedImgSrc = getSizedImageUrl(
      rawImgSrc,
      this.settings.imageSize,
    );
    
    this.$featuredImageWidths.forEach(function(width, idx) { 
      let sizedUrl = rawImgSrc.replace('.png', `_${width}x.png`);
      varientSrcset.push(`${sizedUrl} ${width}w`);
    }); 
    
    this.$featuredImage.attr('src', sizedImgSrc);
    this.$featuredImage.attr('srcset', varientSrcset);

    this.updateProductThumbsActiveState(evt);
  },

  /**
   * Event callback for Theme Editor `section:unload` event
   */
  onUnload() {
    this.$container.off(this.namespace);
  },

  /**
   * Updates the cart via Shopify's Ajax API
   *
   * https://help.shopify.com/themes/development/getting-started/using-ajax-api#add-to-cart
   */
  updateCart(evt) {
    evt.preventDefault();

    const $addToCart = $(selectors.addToCart, this.$container);
    const $addToCartText = $(selectors.addToCartText, this.$container);
    const action = this.$form.attr('action');
    const _self = this;

    $addToCart.prop('disabled', true);
    $addToCartText.html(theme.strings.updateCart);

    $.post(action, this.$form.serialize())
      .done(function() {
        $addToCartText.html(theme.strings.addToCartSuccess);
        _self.cartCount.update();
      })
      .fail(function() {
        $addToCartText.html(theme.strings.addToCartFail);
      })
      .always(function() {
        $addToCart.prop('disabled', false);
      });
  },

  /**
   * Updates the singleOptionSelector select when a thumbnail images is clicked
   */
  updateSingleOptionSelect(evt) {
    evt.preventDefault();

    const value = $(evt.currentTarget).data('value');

    this.$singleOptionThumbnail.removeClass('is-active');
    $(evt.currentTarget).addClass('is-active');

    $(selectors.singleOptionSelector, this.$container)[0].value = value;
    $(selectors.singleOptionSelector, this.$container).trigger('change');
  },

  /**
   * Updates the thumbnail image active state when the singleOptionSelector select is changed
   */
  updateSingleOptionThumbnail(evt) {
    this.$singleOptionThumbnail.removeClass('is-active');
    this.$singleOptionThumbnail.filter(`[data-value="${evt.variant.title}"]`).addClass('is-active');
  },

  /**
   * Updates the active state on product thumbnail images
   */
  updateProductThumbsActiveState(evt) {
    if (this.$productThumbs.length != 0) {;
      this.$productThumbs.removeClass('is-active');
      $(evt.currentTarget).addClass('is-active');
    }
  },
});
